import type { DailyData } from '@/api/dashboard'

export interface MergedRow {
  date: string
  model: string
  username: string
  quota: number
  count: number
  tokens: number
}

export interface AggRow {
  name: string
  quota: number
  count: number
}

export function mergeQuotaRows(data: DailyData[], unknownLabel: string): MergedRow[] {
  const m = new Map<string, MergedRow>()
  for (const d of data || []) {
    const model = d.model_name || unknownLabel
    const username = d.username || unknownLabel
    const k = `${d.date}\t${model}\t${username}`
    const cur = m.get(k) || { date: d.date, model, username, quota: 0, count: 0, tokens: 0 }
    cur.quota += d.quota || 0
    cur.count += d.request_count || d.count || 0
    cur.tokens += d.token_count || 0
    m.set(k, cur)
  }
  return Array.from(m.values())
}

export function aggregateByField(rows: MergedRow[], field: 'model' | 'username'): AggRow[] {
  const map = new Map<string, AggRow>()
  for (const r of rows) {
    const key = field === 'model' ? r.model : r.username
    const cur = map.get(key) || { name: key, quota: 0, count: 0 }
    cur.quota += r.quota
    cur.count += r.count
    map.set(key, cur)
  }
  return Array.from(map.values())
    .filter((x) => x.quota > 0 || x.count > 0)
    .sort((a, b) => b.quota - a.quota)
}

export function buildTopKAndOther(
  items: AggRow[],
  by: 'quota' | 'count',
  k: number,
  otherLabel: string
): AggRow[] {
  const sorted = [...items].sort((a, b) => b[by] - a[by])
  const top = sorted.slice(0, k)
  const rest = sorted.slice(k)
  const otherSum = rest.reduce(
    (s, r) => ({ name: otherLabel, quota: s.quota + r.quota, count: s.count + r.count }),
    { name: otherLabel, quota: 0, count: 0 }
  )
  if (otherSum.quota > 0 || otherSum.count > 0) {
    return [...top, otherSum]
  }
  return top
}

export function rankByField(
  items: AggRow[],
  by: 'quota' | 'count',
  limit: number
): AggRow[] {
  return [...items].sort((a, b) => b[by] - a[by]).slice(0, limit)
}

export function stackedSeriesByDate(
  rows: MergedRow[],
  metric: 'quota' | 'count',
  dimension: 'model' | 'username',
  topK: number,
  otherLabel: string
): { rows: Record<string, string | number>[]; categories: string[] } {
  if (!rows.length) {
    return { rows: [], categories: [] }
  }

  const totals = new Map<string, number>()
  for (const r of rows) {
    const dim = dimension === 'model' ? r.model : r.username
    totals.set(dim, (totals.get(dim) || 0) + r[metric])
  }

  const topNames = Array.from(totals.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, topK)
    .map(([name]) => name)
  const others = Array.from(totals.keys()).filter((n) => !topNames.includes(n))
  const categories = [...topNames, ...(others.length ? [otherLabel] : [])]

  if (categories.length === 0) {
    return { rows: [], categories: [] }
  }

  const dateSet = new Set(rows.map((m) => m.date))
  const dates = Array.from(dateSet).sort()
  const byDate = new Map<string, Map<string, number>>()

  for (const r of rows) {
    const dim = dimension === 'model' ? r.model : r.username
    if (!byDate.has(r.date)) byDate.set(r.date, new Map())
    const dm = byDate.get(r.date)!
    dm.set(dim, (dm.get(dim) || 0) + r[metric])
  }

  const resultRows: Record<string, string | number>[] = dates.map((date) => {
    const row: Record<string, string | number> = { date }
    const mp = byDate.get(date) || new Map()
    for (const name of topNames) {
      row[name] = mp.get(name) || 0
    }
    if (others.length) {
      row[otherLabel] = others.reduce((s, n) => s + (mp.get(n) || 0), 0)
    }
    return row
  })

  return { rows: resultRows, categories }
}

export function hasChartData(data: DailyData[]): boolean {
  if (!data?.length) return false
  return data.some((d) => (d.quota || 0) > 0 || (d.request_count || d.count || 0) > 0)
}

export function tabHasData(
  tab: string,
  merged: MergedRow[],
  byModel: AggRow[],
  byUser: AggRow[]
): boolean {
  switch (tab) {
    case 'quota-dist':
    case 'calls-dist':
      return byModel.some((m) => m.quota > 0 || m.count > 0)
    case 'calls-trend': {
      const { rows } = stackedSeriesByDate(merged, 'count', 'model', 5, '')
      return rows.some((r) => Object.keys(r).some((k) => k !== 'date' && Number(r[k]) > 0))
    }
    case 'model-quota-rank':
      return byModel.some((m) => m.quota > 0)
    case 'model-calls-rank':
      return byModel.some((m) => m.count > 0)
    case 'user-quota-rank':
      return byUser.some((u) => u.quota > 0)
    case 'user-quota-trend': {
      const { rows } = stackedSeriesByDate(merged, 'quota', 'username', 5, '')
      return rows.some((r) => Object.keys(r).some((k) => k !== 'date' && Number(r[k]) > 0))
    }
    default:
      return merged.length > 0
  }
}
